import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  width?: number | string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyMessage?: string;
  /** Chiều cao ước tính mỗi row (px). Dùng cho virtualize auto-height. Default 48. */
  rowHeight?: number;
  /** Bật virtualization thủ công. Mặc định: tự bật khi rows >= 200. */
  virtualize?: boolean;
  /** Chiều cao vùng cuộn (virtualize). Default 600. */
  viewportHeight?: number;
  /** Số row render thêm ở trên/dưới cửa sổ. Default 8. */
  overscan?: number;
  /** Số dòng mỗi trang. undefined = không phân trang (render all). */
  pageSize?: number;
  /** Sort mặc định */
  defaultSort?: { key: string; dir: "asc" | "desc" };
  /** Cho phép sort nhiều cột cùng lúc (Shift+click để thêm cột phụ). Mặc định off. */
  multiSort?: boolean;
  /** Gọi mỗi khi thứ tự sort đổi — nhận mảng rows đã sort theo đúng thứ tự hiển thị. */
  onSortedRowsChange?: (rows: T[]) => void;
  /** Rows của trang hiện tại, sau sort; dùng cho thao tác chọn trang. */
  onVisibleRowsChange?: (rows: T[]) => void;
}

/**
 * Một <tr> đơn lẻ — `React.memo` để row không bị re-render khi các row khác / parent
 * thay đổi (miễn sao `row`, `columns`, và `rowKey` không đổi).
 */
interface DataRowProps<T> {
  row: T;
  columns: Column<T>[];
  style?: React.CSSProperties;
}

function DataRowInner<T>({ row, columns, style }: DataRowProps<T>) {
  return (
    <tr style={style}>
      {columns.map((col) => (
        <td key={col.key} style={col.align ? { textAlign: col.align } : undefined}>{col.render(row)}</td>
      ))}
    </tr>
  );
}
const DataRow = React.memo(DataRowInner) as typeof DataRowInner;

/* ── Sort helpers ── */
function getSortValue(row: any, key: string): any {
  if (key in row) return row[key];
  return undefined;
}

function compareValues(a: any, b: any): number {
  // ponytail: null/undefined/"" (missing tier) được xử lý ở comparator ngoài,
  // nơi chúng luôn sort FIRST và miễn nhiễm với dir (asc/desc). Ở đây chỉ còn so
  // sánh giá trị có thật. Giữ guard thủ công cho an toàn (không nên tới được).
  if (a == null || b == null || a === "" || b === "") return 0;

  // Number
  if (typeof a === "number" && typeof b === "number") return a - b;

  // Try parse as number
  const na = Number(a), nb = Number(b);
  if (!isNaN(na) && !isNaN(nb)) return na - nb;

  // Try parse as date
  const da = Date.parse(String(a)), db = Date.parse(String(b));
  if (!isNaN(da) && !isNaN(db)) return da - db;

  // String
  return String(a).localeCompare(String(b), "vi");
}

type SortDir = "asc" | "desc" | "none";

/** Một mục sort trong chuỗi multi-sort (chỉ asc/desc, không có "none"). */
interface SortEntry {
  key: string;
  dir: "asc" | "desc";
}

function nextSortDir(current: SortDir): SortDir {
  if (current === "none") return "asc";
  if (current === "asc") return "desc";
  return "none";
}

const SORT_ICONS: Record<SortDir, string> = { none: "↕", asc: "↑", desc: "↓" };

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Header có thể sort — vẽ icon mũi tên + badge thứ tự (①②③) khi multi-sort.
 * Click thường = sort cột này; Shift+click (khi multiSort) = thêm cột phụ.
 */
function SortableHeader<T>({
  col,
  sortState,
  multiSort,
  onSort,
}: {
  col: Column<T>;
  sortState: SortEntry[];
  multiSort: boolean;
  onSort: (col: Column<T>, additive: boolean) => void;
}) {
  const idx = sortState.findIndex((s) => s.key === col.key);
  const entry = idx >= 0 ? sortState[idx] : null;
  const dir: SortDir = entry ? entry.dir : "none";
  // Badge thứ tự chỉ hiện khi đang multi-sort (>1 cột).
  const showOrder = multiSort && sortState.length > 1 && idx >= 0;
  return (
    <th
      className={`${col.sortable ? "sortable" : ""} ${entry ? "sorted" : ""}`}
      style={col.align ? { textAlign: col.align } : undefined}
      onClick={(e) => onSort(col, e.shiftKey)}
      title={col.sortable && multiSort ? "Click để sort · Shift+click để thêm cột phụ" : undefined}
    >
      {col.header}
      {col.sortable && (
        <span className="sort-icon">
          {SORT_ICONS[dir]}
          {showOrder && <sup style={{ fontSize: "0.7em", marginLeft: 1 }}>{idx + 1}</sup>}
        </span>
      )}
    </th>
  );
}

/**
 * Bảng dữ liệu chung — hỗ trợ pagination, sortable columns, sticky header.
 * Tự động virtualize khi `rows.length >= 200` và không có pageSize.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage = "Không có dữ liệu",
  rowHeight = 48,
  virtualize,
  viewportHeight = 600,
  overscan = 8,
  pageSize: initialPageSize,
  defaultSort,
  multiSort = false,
  onSortedRowsChange,
  onVisibleRowsChange,
}: DataTableProps<T>) {
  // Sort state — mảng có thứ tự, hỗ trợ multi-sort. Rỗng = không sort.
  const [sortState, setSortState] = useState<SortEntry[]>(
    defaultSort ? [{ key: defaultSort.key, dir: defaultSort.dir }] : [],
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset page when rows change
  useEffect(() => { setCurrentPage(1); }, [rows.length]);

  // Sort data — so sánh lần lượt theo từng cột trong sortState (multi-level).
  // Missing tier (null/undefined/"") luôn sort FIRST, miễn nhiễm với dir.
  // Phù hợp UX "hiện dữ liệu thiếu lên đầu để batch-edit" (vd: lọc SP chưa có danh mục).
  const sortedRows = useMemo(() => {
    if (sortState.length === 0) return rows;
    return [...rows].sort((a, b) => {
      for (const s of sortState) {
        const va = getSortValue(a, s.key);
        const vb = getSortValue(b, s.key);
        const aMissing = va == null || va === "";
        const bMissing = vb == null || vb === "";
        if (aMissing && bMissing) continue;        // tie, chạy sang cột sort kế
        if (aMissing) return -1;                    // missing luôn trước, mọi dir
        if (bMissing) return 1;
        const cmp = compareValues(va, vb);
        if (cmp !== 0) return s.dir === "desc" ? -cmp : cmp;
      }
      return 0;
    });
  }, [rows, sortState]);

  // Báo thứ tự sort hiện tại ra ngoài (vd: để xuất PDF đúng thứ tự đang hiển thị).
  const onSortedRef = useRef(onSortedRowsChange);
  onSortedRef.current = onSortedRowsChange;
  useEffect(() => { onSortedRef.current?.(sortedRows); }, [sortedRows]);

  // Paginate
  const hasPagination = pageSize != null && pageSize > 0;
  const totalPages = hasPagination ? Math.max(1, Math.ceil(sortedRows.length / pageSize!)) : 1;
  const displayRows = useMemo(
    () => hasPagination
      ? sortedRows.slice((currentPage - 1) * pageSize!, currentPage * pageSize!)
      : sortedRows,
    [sortedRows, hasPagination, currentPage, pageSize],
  );

  const onVisibleRowsRef = useRef(onVisibleRowsChange);
  onVisibleRowsRef.current = onVisibleRowsChange;
  useEffect(() => { onVisibleRowsRef.current?.(displayRows); }, [displayRows]);

  // Clamp page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleSort = (col: Column<T>, additive: boolean) => {
    if (!col.sortable) return;
    setSortState((prev) => {
      const idx = prev.findIndex((s) => s.key === col.key);
      // Shift+click (chỉ khi bật multiSort): thêm/đổi cột phụ, giữ các cột khác.
      if (additive && multiSort) {
        if (idx === -1) return [...prev, { key: col.key, dir: "asc" }];
        const cur = prev[idx];
        if (cur.dir === "asc") {
          const next = [...prev]; next[idx] = { ...cur, dir: "desc" }; return next;
        }
        // desc → bỏ cột này khỏi chuỗi sort
        return prev.filter((_, i) => i !== idx);
      }
      // Click thường: thao tác trên 1 cột, bỏ các cột khác.
      const isOnlyThis = prev.length === 1 && idx === 0;
      if (isOnlyThis) {
        const next = nextSortDir(prev[0].dir);
        return next === "none" ? [] : [{ key: col.key, dir: next }];
      }
      return [{ key: col.key, dir: "asc" }];
    });
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Auto-enable virtualize khi có nhiều row và không có pagination.
  const shouldVirtualize = !hasPagination && (virtualize ?? rows.length >= 200);

  if (shouldVirtualize) {
    return (
      <VirtualizedTable
        columns={columns}
        rows={sortedRows}
        rowKey={rowKey}
        rowHeight={rowHeight}
        viewportHeight={viewportHeight}
        overscan={overscan}
        sortState={sortState}
        multiSort={multiSort}
        onSort={handleSort}
      />
    );
  }

  return (
    <div>
      <div className="table-wrap" style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)", borderRadius: 12 }}>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <SortableHeader
                  key={col.key}
                  col={col}
                  sortState={sortState}
                  multiSort={multiSort}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center", opacity: 0.5 }}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              displayRows.map((row) => (
                <DataRow key={rowKey(row)} row={row} columns={columns} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {hasPagination && sortedRows.length > 0 && (
        <div className="data-table-pagination">
          <div className="page-info">
            Tổng {sortedRows.length} · Trang {currentPage} / {totalPages}
          </div>
          <div className="page-size">
            <span style={{ marginRight: 6 }}>Hiển thị</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Trước
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Sau
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Table có windowing: chỉ render các row trong cửa sổ viewport + overscan.
 * Hoạt động thuần React, không phụ thuộc thư viện ngoài.
 */
function VirtualizedTable<T>({
  columns,
  rows,
  rowKey,
  rowHeight,
  viewportHeight,
  overscan,
  sortState,
  multiSort,
  onSort,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  rowHeight: number;
  viewportHeight: number;
  overscan: number;
  sortState: SortEntry[];
  multiSort: boolean;
  onSort: (col: Column<T>, additive: boolean) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  // Throttle scroll qua rAF để tránh setState quá nhiều.
  const rafRef = useRef<number | null>(null);
  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (scrollerRef.current) setScrollTop(scrollerRef.current.scrollTop);
    });
  }, []);

  useEffect(() => () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
  }, []);

  const totalHeight = rows.length * rowHeight;
  const startIdx = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endIdx = Math.min(
    rows.length,
    Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan,
  );
  const visibleRows = useMemo(() => rows.slice(startIdx, endIdx), [rows, startIdx, endIdx]);
  const offsetY = startIdx * rowHeight;

  return (
    <div
      className="table-wrap table-virtual"
      ref={scrollerRef}
      onScroll={handleScroll}
      style={{ height: viewportHeight, overflow: "auto", position: "relative" }}
    >
      <table className="data-table" style={{ width: "100%", tableLayout: "fixed" }}>
        <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
          <tr>
            {columns.map((col) => (
              <SortableHeader
                key={col.key}
                col={col}
                sortState={sortState}
                multiSort={multiSort}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
      </table>
      <div style={{ height: totalHeight, position: "relative" }}>
        <table
          className="data-table"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            tableLayout: "fixed",
            transform: `translateY(${offsetY}px)`,
          }}
        >
          <tbody>
            {visibleRows.map((row) => (
              <DataRow
                key={rowKey(row)}
                row={row}
                columns={columns}
                style={{ height: rowHeight }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
